import React from 'react';
import { Phone, MessageSquare, Crown, User, Mail } from 'lucide-react';
import { formatPhoneNumber, getPhoneLink, getWhatsAppLink } from '../../utils/phoneUtils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { isPro as checkIsPro } from '../../utils/planPermissions';

interface ClientContactInfoProps {
  clientId?: string;
  clientName: string | null;
  phoneNumber: string;
  // Media docena de listas (visitas, insatisfechos…) traen el país como `string | undefined`
  // porque su fila lo tiene opcional; el tipo pedía `string | null` estricto. Las tres
  // utilidades de phoneUtils ya resuelven con `countryCode || ''`, así que aceptar los dos
  // huecos no cambia lo que se pinta.
  countryCode?: string | null;
  email?: string | null;
  iconSize?: 'sm' | 'md' | 'lg';
  showName?: boolean;
  nameAsLink?: boolean;
  truncate?: boolean;
  className?: string;
  showAvatar?: boolean;   // false → omite el círculo (cuando ya se renderiza un ClientAvatar al lado)
  avatarUrl?: string | null; // foto real (p. ej. la de WhatsApp del dueño en el admin); sin ella, el icono genérico
}

const ClientContactInfo: React.FC<ClientContactInfoProps> = ({
  clientId,
  clientName,
  phoneNumber,
  countryCode,
  email,
  iconSize = 'md',
  showName = true,
  nameAsLink = true,
  truncate = true,
  className = '',
  showAvatar = true,
  avatarUrl = null
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Lógica de permisos (PRO/Premium o admin)
  const isAdmin = currentUser?.role === 'admin';
  const hasProAccess = checkIsPro(currentUser?.subscription) || isAdmin;

  // Manejo de tamaños de íconos
  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  const iconClass = iconSizes[iconSize] || iconSizes.md;
  const textSize = iconSize === 'sm' ? 'text-xs' : iconSize === 'lg' ? 'text-base' : 'text-sm';

  // Navegación al historial del cliente
  const handleNavigateToClient = (e: React.MouseEvent) => {
    if (clientId) {
      e.preventDefault();
      navigate(`/dashboard/clients/${clientId}/history`);
    }
  };

  // Click para actualizar plan
  const handleUpgradeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate('/dashboard/subscription');
  };

  // Formateo del número de teléfono (ofuscado si no es PRO/Premium/admin)
  const displayPhoneNumber = hasProAccess
    ? formatPhoneNumber(countryCode, phoneNumber)
    : formatPhoneNumber(countryCode, phoneNumber).replace(/\d/g, '•');

  return (
    <div className={`flex items-center ${className}`}>
      {showAvatar && (
        <div className="flex-shrink-0 h-10 w-10 bg-canvas rounded-full flex items-center justify-center overflow-hidden">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="h-10 w-10 object-cover" loading="lazy" data-testid="contacto-avatar"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
          ) : (
            <User className="h-6 w-6 text-content-3" />
          )}
        </div>
      )}

      <div className={`${showAvatar ? 'ml-4' : ''} min-w-0`}>
        {showName && (
          <div className={`${textSize} font-medium text-content ${truncate ? 'truncate' : ''}`}>
            {nameAsLink && clientId ? (
              <a
                href={`/dashboard/clients/${clientId}/history`}
                onClick={handleNavigateToClient}
                className="hover:text-accent focus:outline-none focus:ring-2 focus:ring-accent rounded"
                title={`Ver historial de ${clientName || 'este cliente'}`}
              >
                {clientName || 'Sin nombre'}
              </a>
            ) : (
              clientName || 'Sin nombre'
            )}
          </div>
        )}
        
        {/* Mostrar Correo */}
        {email && (
            <div className={`text-sm text-content-3 flex items-center truncate max-w-[200px]`} title={email}>
                <Mail className="h-3.5 w-3.5 mr-1.5 text-content-3 flex-shrink-0" />
                <span>{email}</span>
            </div>
        )}

        <div className={`flex items-center ${email ? 'mt-1' : ''} ${textSize} text-content-3 flex-wrap gap-x-2 gap-y-1`}>
          {hasProAccess ? (
            <>
              <a
                href={getPhoneLink(countryCode, phoneNumber)}
                className="inline-flex items-center hover:text-accent text-content-3"
                onClick={(e) => e.stopPropagation()}
                title={`Llamar a ${formatPhoneNumber(countryCode, phoneNumber)}`}
              >
                <Phone className={`${iconClass} mr-1 flex-shrink-0`} />
                {formatPhoneNumber(countryCode, phoneNumber)}
              </a>
              
              <a
                href={getWhatsAppLink(countryCode, phoneNumber)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-green-600 dark:text-green-400 hover:text-green-700 dark:text-green-300"
                onClick={(e) => e.stopPropagation()}
                title={`Enviar WhatsApp a ${formatPhoneNumber(countryCode, phoneNumber)}`}
              >
                <MessageSquare className={`${iconClass}`} />
              </a>
            </>
          ) : (
            <div className="flex items-center gap-x-2 gap-y-1 relative group">
              <div className="inline-flex items-center text-content-3">
                <Phone className={`${iconClass} mr-1 flex-shrink-0`} />
                <span className="blur-sm select-none filter">{displayPhoneNumber}</span>
              </div>
              
              <div className="inline-flex items-center text-content-3">
                <MessageSquare className={`${iconClass}`} />
              </div>
              
              <span className="absolute left-0 right-0 -bottom-4 hidden group-hover:block text-xs font-medium text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-500/10 px-1 py-0.5 rounded border border-yellow-200 whitespace-nowrap z-10">
                <Crown className="h-2.5 w-2.5 inline mr-0.5 text-yellow-500" />
                Función PRO
              </span>
              
              <button
                onClick={handleUpgradeClick}
                className="ml-1 px-1.5 py-0.5 text-xs rounded bg-yellow-50 dark:bg-yellow-500/10 text-yellow-700 dark:text-yellow-300 border border-yellow-100 hover:bg-yellow-100 dark:hover:bg-yellow-500/20 transition-colors inline-flex items-center"
              >
                <Crown className={`${iconSize === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} mr-0.5 text-yellow-500`} />
                PRO
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientContactInfo;